import * as React from "react";
import { ToggleSwitch } from "../../toggle-switch/ToggleSwitch.component";
import { composeMatrix4, decomposeMatrix4, useInteractionModelsStore, sendCoordinateTypeMessage } from "../../../store/interaction-models.store";
import { useEffect, useState } from "react";
import { createXYZInputs, eulerToStringCoordinates, getLinkedScale, modifyStringCoordinates, stringCoordinatesToEuler, stringCoordinatesToVector3, vector3ToStringCoordinates } from "../ModelSettingsControl.util";
import { EnableModelTransformHandlesAction, InteractionFacet } from "flavr-common";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Vector3 } from "three";
import { Euler, Matrix4, Quaternion } from "flavr-math";
import { RequestObject3DAnswerMessage, RequestObject3DMessage } from "../../../../messages/rendering/MeshMessages";
import { useTranslation } from "react-i18next";
const lastMatrixAndRotationValuesMap = new Map();
export function ModelSettingsControlTransform({ connection }) {
    const [model, setModel] = useState(undefined);
    const [object3D, setObject3D] = useState(undefined);
    const [currentPosition, setCurrentPosition] = useState();
    const [currentRotation, setCurrentRotation] = useState();
    const [currentScale, setCurrentScale] = useState();
    const [sendUpdatesTrigger, setSendUpdatesTrigger] = useState(0);
    const [scaleLinked, setScaleLinked] = useState(true);
    const [scalable, setScalable] = useState(false);
    const [localCoordinates, setLocalCoordinates] = useState(false);
    const modelStore = useInteractionModelsStore();
    const { t } = useTranslation();
    useEffect(() => {
        updateModel();
    }, [modelStore]);
    useEffect(() => {
        setScaleLinked(true);
    }, [modelStore.currentModelUID]);
    const updateModel = () => {
        var _a;
        const store = useInteractionModelsStore.getState();
        if (store.currentModelUID) {
            const model = store.models.find((model) => model.helper.getUID() === store.currentModelUID);
            connection
                .askByMessageType(new RequestObject3DMessage((_a = model === null || model === void 0 ? void 0 : model.helper.getUID()) !== null && _a !== void 0 ? _a : 0), RequestObject3DAnswerMessage)
                .then((response) => {
                setObject3D(response.object);
            })
                .catch((error) => {
                console.error("Error getting object3D", error);
                setObject3D(undefined);
            })
                .finally(() => {
                setModel(model);
            });
        }
    };
    useEffect(() => {
        var _a, _b;
        if (model) {
            const coordinates = decomposeMatrix4((_a = model.matrix) !== null && _a !== void 0 ? _a : new Matrix4());
            setCurrentScale(vector3ToStringCoordinates(coordinates.scale));
            if (!localCoordinates) {
                setCurrentPosition(vector3ToStringCoordinates(coordinates.position));
                const lastValues = lastMatrixAndRotationValuesMap.get(model.helper.getUID());
                if (!lastValues || !lastValues.rotation || !((_b = model.matrix) === null || _b === void 0 ? void 0 : _b.equals(lastValues.matrix))) {
                    setCurrentRotation(eulerToStringCoordinates(coordinates.rotation));
                }
                else {
                    setCurrentRotation(lastValues.rotation);
                }
            }
            const interactionFacet = model.helper.getFacet(InteractionFacet);
            let isScalable = false;
            if (interactionFacet !== undefined) {
                const action = interactionFacet.onClick.find((value) => {
                    return value.action === EnableModelTransformHandlesAction.typestatic;
                });
                if (action !== undefined)
                    isScalable = action.scalable;
            }
            setScalable(isScalable);
        }
        else {
            setCurrentPosition({ x: "0", y: "0", z: "0" });
            setCurrentRotation({ x: "0", y: "0", z: "0" });
            setCurrentScale({ x: "1", y: "1", z: "1" });
            setScalable(false);
        }
    }, [model]);
    useEffect(() => {
        var _a;
        if (model) {
            updateModel();
            let matrix = (_a = model.matrix) !== null && _a !== void 0 ? _a : new Matrix4();
            if (localCoordinates && object3D) {
                const localMatrix = object3D.matrix;
                if (localMatrix === undefined) {
                    console.error("Could not convert global to local matrix");
                    return;
                }
                matrix = localMatrix;
            }
            const coordinates = decomposeMatrix4(matrix !== null && matrix !== void 0 ? matrix : new Matrix4());
            setCurrentPosition(vector3ToStringCoordinates(coordinates.position));
            setCurrentRotation(eulerToStringCoordinates(coordinates.rotation));
        }
    }, [localCoordinates]);
    useEffect(() => {
        if (model && currentPosition && currentRotation && currentScale) {
            updateValuesToModel({ position: currentPosition, rotation: currentRotation, scale: currentScale });
        }
    }, [sendUpdatesTrigger]);
    const sendUpdates = () => {
        setSendUpdatesTrigger((prev) => prev + 1);
    };
    const updateValuesToModel = (values) => {
        if (!model) {
            console.error("No model to update");
            return;
        }
        let pos = values.position ? stringCoordinatesToVector3(values.position) : new Vector3();
        let rot = values.rotation ? stringCoordinatesToEuler(values.rotation) : new Euler();
        if (localCoordinates && object3D) {
            const euler = values.rotation ? stringCoordinatesToEuler(values.rotation) : new Euler();
            const tempM4 = composeMatrix4({
                position: values.position ? stringCoordinatesToVector3(values.position) : new Vector3(),
                rotation: euler,
                scale: values.scale ? stringCoordinatesToVector3(values.scale) : new Vector3(1, 1, 1),
                quaternion: new Quaternion().setFromEuler(euler)
            });
            const transformed = object3D.matrixWorld.multiply(tempM4);
            if (transformed === undefined) {
                console.error("Could not convert local to global matrix");
                return;
            }
            const tempPos = new Vector3();
            const tempRot = new Quaternion();
            transformed.decompose(tempPos, tempRot, new Vector3());
            pos = tempPos;
            rot = new Euler().setFromQuaternion(tempRot);
        }
        const m4 = composeMatrix4({
            position: pos,
            rotation: rot,
            scale: values.scale ? stringCoordinatesToVector3(values.scale) : stringCoordinatesToVector3(currentScale),
            quaternion: new Quaternion().setFromEuler(rot)
        });
        const newModel = Object.assign({}, model);
        newModel.matrix = m4;
        lastMatrixAndRotationValuesMap.set(newModel.helper.getUID(), { matrix: m4, rotation: currentRotation });
        modelStore.updateModel(newModel);
    };
    const modifyPosition = (value) => {
        var _a, _b, _c;
        if (localCoordinates && object3D) {
            updateValuesToModel({ position: { x: (_a = value.x) !== null && _a !== void 0 ? _a : "0", y: (_b = value.y) !== null && _b !== void 0 ? _b : "0", z: (_c = value.z) !== null && _c !== void 0 ? _c : "0" } });
        }
        else {
            setCurrentPosition((prev) => modifyStringCoordinates(prev, value));
            sendUpdates();
        }
    };
    const modifyRotation = (value) => {
        var _a, _b, _c;
        if (localCoordinates) {
            updateValuesToModel({ rotation: { x: (_a = value.x) !== null && _a !== void 0 ? _a : "0", y: (_b = value.y) !== null && _b !== void 0 ? _b : "0", z: (_c = value.z) !== null && _c !== void 0 ? _c : "0" } });
        }
        else {
            setCurrentRotation((prev) => modifyStringCoordinates(prev, value));
            sendUpdates();
        }
    };
    const modifyScale = (value) => {
        setCurrentScale((prev) => {
            const v3Prev = stringCoordinatesToVector3(prev);
            if (scaleLinked && prev) {
                if ((value.x !== undefined && value.y !== undefined) ||
                    (value.x !== undefined && value.z !== undefined) ||
                    (value.y !== undefined && value.z !== undefined)) {
                    // If scaleLinked is true, only one of the values can be set at a time
                    console.error("Cannot scale uniformly and non-uniformly at the same time");
                    return undefined;
                }
                if (value.x !== undefined) {
                    return getLinkedScale("x", value.x, v3Prev);
                }
                else if (value.y !== undefined) {
                    return getLinkedScale("y", value.y, v3Prev);
                }
                else if (value.z !== undefined) {
                    return getLinkedScale("z", value.z, v3Prev);
                }
                else {
                    return prev;
                }
            }
            else {
                return modifyStringCoordinates(prev, value, { x: "1", y: "1", z: "1" });
            }
        });
        sendUpdates();
    };
    const coordinateOptionWorld = t("objecteditor:switch-coordinate-system-option-world", "World (absolute)");
    const coordinateOptionLocal = t("objecteditor:switch-coordinate-system-option-local", "Local (relative)");
    const coordinateSystemCurrentWorld = t("objecteditor:switch-coordinate-system-tooltip-current-world", "World coordinate system");
    const coordinateSystemCurrentLocal = t("objecteditor:switch-coordinate-system-tooltip-current-local", "Local coordinate system");
    const coordinateSystemTargetWorld = t("objecteditor:switch-coordinate-system-tooltip-target-world", "world coordinates");
    const coordinateSystemTargetLower = t("objecteditor:switch-coordinate-system-tooltip-target-local", "local coordinates");
    return (React.createElement(React.Fragment, null,
        React.createElement("div", { className: "model-settings-control-content-row" },
            React.createElement("div", { className: "model-settings-control-content-row-title" },
                React.createElement("span", null, t("objecteditor:row-title-coordinate-system", "Coordinate System"))),
            React.createElement("div", { "data-tooltip": t("objecteditor:switch-coordinate-system-tooltip", "{{currentSystem}} is currently being used for position and rotation.\nClick to switch to {{targetSystem}}", {
                    currentSystem: localCoordinates ? coordinateSystemCurrentLocal : coordinateSystemCurrentWorld,
                    targetSystem: localCoordinates ? coordinateSystemTargetWorld : coordinateSystemTargetLower
                }) },
                React.createElement(ToggleSwitch, { option1: coordinateOptionWorld, option2: coordinateOptionLocal, selected: localCoordinates ? coordinateOptionLocal : coordinateOptionWorld, onChange: (option) => {
                        const optionIsLocal = option === coordinateOptionLocal;
                        setLocalCoordinates(optionIsLocal);
                        if (modelStore.currentModelUID !== undefined)
                            sendCoordinateTypeMessage(modelStore.currentModelUID, optionIsLocal);
                    } }))),
        React.createElement("div", { className: "model-settings-control-content-row" },
            React.createElement("div", { className: "model-settings-control-content-row-title" },
                React.createElement("span", null, t("objecteditor:row-title-position", "Position"))),
            createXYZInputs(currentPosition, modifyPosition, {
                relative: localCoordinates,
                tooltip: (axis) => t("objecteditor:input-position-tooltip", "Move along the {{axis}}-axis", { axis: axis })
            })),
        React.createElement("div", { className: "model-settings-control-content-row" },
            React.createElement("div", { className: "model-settings-control-content-row-title" },
                React.createElement("span", null, t("objecteditor:row-title-rotation", "Rotation"))),
            createXYZInputs(currentRotation, modifyRotation, {
                stepSize: 1,
                relative: localCoordinates,
                tooltip: (axis) => t("objecteditor:input-rotation-tooltip", "Rotate around the {{axis}}-axis", { axis: axis })
            })),
        React.createElement("div", { className: "model-settings-control-row-separator" }),
        React.createElement("div", { className: "model-settings-control-content-row" },
            React.createElement("div", { className: "model-settings-control-content-row-title" },
                React.createElement("span", null, t("objecteditor:row-title-scale", "Scale")),
                React.createElement("button", { className: `icon-button-small ${scaleLinked ? "scale-button-active" : "scale-button-inactive"}`, onClick: () => {
                        setScaleLinked((prev) => !prev);
                    }, disabled: !scalable, "data-tooltip": scaleLinked
                        ? t("objecteditor:button-link-scaling-linked-tooltip", "Scale uniformly\n(Click to scale each dimension individually)")
                        : t("objecteditor:button-link-scaling-unlinked-tooltip", "Scale non-uniformly\n(Click to scale uniformly)"), "data-tooltip-disabled": t("objecteditor:button-link-scaling-tooltip-disabled", "Scaling is disabled for this model") },
                    React.createElement(FontAwesomeIcon, { icon: ["fas", scaleLinked ? "link" : "unlink"] })),
                React.createElement("div", { style: { flexGrow: 1 } })),
            createXYZInputs(currentScale, modifyScale, {
                defaultValue: "1",
                disabled: !scalable,
                tooltip: (axis) => t("objecteditor:input-scale-tooltip", "Scale along the {{axis}}-axis", { axis: axis }),
                disabledTooltip: t("objecteditor:input-scale-tooltip-disabled", "Scaling is disabled for this model"),
                tooltipPosition: "top-right"
            }))));
}
